// React Query hooks over ContractModelService (contract model v2). Every read
// is keyed under ['cm', programId, ...] and every write invalidates the whole
// ['cm', programId] prefix: one write can mint or retire dates anywhere in the
// program (a team rule, a linked exercise, a setting), so a narrower
// invalidation would leave some page showing yesterday's numbers.
//
// Each request wraps pangaea's own message as `body`; these hooks take and
// return those inner messages so callers never see the wrapper.
import { ContractModelTypes as CM } from '@mono/proto-clients';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { contractModelClient } from '../../../lib/connect/clients';
export const OccurrenceWindow = CM.OccurrenceWindow;
export const SowGapChoice = CM.SowGapChoice;
export const CM_KEY = 'cm';
const STALE = 15000;
export const cmKeys = {
    all: (programId)=>[
            CM_KEY,
            programId
        ],
    duties: (programId, includeRules = false)=>[
            CM_KEY,
            programId,
            'duties',
            includeRules
        ],
    duty: (programId, dutyId)=>[
            CM_KEY,
            programId,
            'duty',
            dutyId
        ],
    occurrences: (programId, req)=>[
            CM_KEY,
            programId,
            'occurrences',
            JSON.stringify(req)
        ],
    occurrence: (programId, occId)=>[
            CM_KEY,
            programId,
            'occurrence',
            occId
        ],
    decisions: (programId, req)=>[
            CM_KEY,
            programId,
            'decisions',
            JSON.stringify(req)
        ],
    workstreams: (programId)=>[
            CM_KEY,
            programId,
            'workstreams'
        ],
    workstream: (programId, wsId)=>[
            CM_KEY,
            programId,
            'workstream',
            wsId
        ],
    settings: (programId)=>[
            CM_KEY,
            programId,
            'settings'
        ]
};
// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
export function useDuties(programId, { includeRules = false } = {}) {
    return useQuery({
        queryKey: cmKeys.duties(programId, includeRules),
        enabled: !!programId,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listDuties({
                body: {
                    programId,
                    includeRules
                }
            });
            return resp.body ?? new CM.ListDutiesResponse();
        }
    });
}
export function useDuty(programId, dutyId) {
    return useQuery({
        queryKey: cmKeys.duty(programId, dutyId ?? ''),
        enabled: !!programId && !!dutyId,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.getDuty({
                body: {
                    programId,
                    dutyId
                }
            });
            return resp.body ?? new CM.GetDutyResponse();
        }
    });
}
/** GetDuty for several deliverables at once (their SOW assignments and checks). */ export function useDutiesDetail(programId, dutyIds) {
    return useQueries({
        queries: dutyIds.map((dutyId)=>({
                queryKey: cmKeys.duty(programId, dutyId),
                enabled: !!programId && !!dutyId,
                staleTime: STALE,
                queryFn: async ()=>{
                    const resp = await contractModelClient.getDuty({
                        body: {
                            programId,
                            dutyId
                        }
                    });
                    return resp.body ?? new CM.GetDutyResponse();
                }
            }))
    });
}
async function listOccurrences(programId, req) {
    const resp = await contractModelClient.listOccurrences({
        body: {
            programId,
            ...req
        }
    });
    return resp.body ?? new CM.ListOccurrencesResponse();
}
export function useOccurrences(programId, req, { enabled = true } = {}) {
    return useQuery({
        queryKey: cmKeys.occurrences(programId, req),
        enabled: !!programId && enabled,
        staleTime: STALE,
        queryFn: ()=>listOccurrences(programId, req)
    });
}
/**
 * One ListOccurrences per program, folded on the client (D113's portfolio
 * fan-out). Keyed per program so each program's slice is shared with its own
 * pages' cache.
 */ export function useOccurrencesFanOut(programIds, req) {
    return useQueries({
        queries: programIds.map((programId)=>({
                queryKey: cmKeys.occurrences(programId, req),
                enabled: !!programId,
                staleTime: STALE,
                queryFn: ()=>listOccurrences(programId, req)
            }))
    });
}
/** Several ListOccurrences on one program at once (e.g. each team's window). */ export function useOccurrencesMany(programId, reqs) {
    return useQueries({
        queries: reqs.map((req)=>({
                queryKey: cmKeys.occurrences(programId, req),
                enabled: !!programId,
                staleTime: STALE,
                queryFn: ()=>listOccurrences(programId, req)
            }))
    });
}
export function useOccurrence(programId, occId, asOf = '') {
    return useQuery({
        queryKey: cmKeys.occurrence(programId, occId ?? ''),
        enabled: !!programId && !!occId,
        staleTime: STALE,
        retry: false,
        queryFn: async ()=>{
            const resp = await contractModelClient.getOccurrence({
                body: {
                    programId,
                    occurrenceId: occId,
                    asOf
                }
            });
            return resp.body ?? new CM.GetOccurrenceResponse();
        }
    });
}
export function useOccurrenceDecisions(programId, req, { enabled = true } = {}) {
    return useQuery({
        queryKey: cmKeys.decisions(programId, req),
        enabled: !!programId && enabled,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listOccurrenceDecisions({
                body: {
                    programId,
                    ...req
                }
            });
            return resp.body ?? new CM.ListOccurrenceDecisionsResponse();
        }
    });
}
/** The program's open proposals, by the date they are on. */ export function useAwaitingApproval(programId) {
    const query = useOccurrenceDecisions(programId, {
        openOnly: true
    });
    const { data } = query;
    const maps = useMemo(()=>{
        const occById = new Map((data?.occurrences ?? []).map((o)=>[
                o.id,
                o
            ]));
        const byOccurrence = new Map();
        for (const d of data?.decisions ?? [])byOccurrence.set(d.occurrenceId, d);
        return {
            occById,
            byOccurrence
        };
    }, [
        data
    ]);
    return {
        query,
        ...maps
    };
}
export function useWorkstreams(programId, { enabled = true } = {}) {
    return useQuery({
        queryKey: cmKeys.workstreams(programId),
        enabled: !!programId && enabled,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.listWorkstreams({
                body: {
                    programId
                }
            });
            return resp.body?.workstreams ?? [];
        }
    });
}
export function useWorkstream(programId, wsId) {
    return useQuery({
        queryKey: cmKeys.workstream(programId, wsId ?? ''),
        enabled: !!programId && !!wsId,
        staleTime: STALE,
        retry: false,
        queryFn: async ()=>{
            const resp = await contractModelClient.getWorkstream({
                body: {
                    programId,
                    workstreamId: wsId
                }
            });
            return resp.body ?? new CM.GetWorkstreamResponse();
        }
    });
}
/** Every team's GetWorkstream at once (SOWs, lineage, lines), for pickers and grouping. */ export function useWorkstreamDetails(programId, wsIds) {
    return useQueries({
        queries: wsIds.map((wsId)=>({
                queryKey: cmKeys.workstream(programId, wsId),
                enabled: !!programId && !!wsId,
                staleTime: STALE,
                queryFn: async ()=>{
                    const resp = await contractModelClient.getWorkstream({
                        body: {
                            programId,
                            workstreamId: wsId
                        }
                    });
                    return resp.body ?? new CM.GetWorkstreamResponse();
                }
            }))
    });
}
export function useDeliverableSettings(programId) {
    return useQuery({
        queryKey: cmKeys.settings(programId),
        enabled: !!programId,
        staleTime: STALE,
        queryFn: async ()=>{
            const resp = await contractModelClient.getDeliverableSettings({
                body: {
                    programId
                }
            });
            return resp.body?.settings ?? new CM.DeliverableSettings();
        }
    });
}
function useCmMutation(programId, fn, { success, errors = 'toast', failure = 'Could not save' } = {}) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: (r)=>{
            void qc.invalidateQueries({
                queryKey: cmKeys.all(programId)
            });
            if (success) toast.success(typeof success === 'function' ? success(r) : success);
        },
        onError: (err)=>{
            if (errors === 'toast') toast.error(apiErrorMessage(err, failure));
        }
    });
}
export function useCreateDuty(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.createDuty({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateDutyResponse(), opts);
}
export function useUpdateDuty(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.updateDuty({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.UpdateDutyResponse(), opts);
}
export function useDeleteDuty(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.deleteDuty({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.DeleteDutyResponse(), opts);
}
export function useCreateRule(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.createScheduleRule({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateScheduleRuleResponse(), opts);
}
export function useUpdateRule(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.updateScheduleRule({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.UpdateScheduleRuleResponse(), opts);
}
export function useDeleteRule(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.deleteScheduleRule({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.DeleteScheduleRuleResponse(), opts);
}
export function useAddOccurrence(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.addOccurrence({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.AddOccurrenceResponse(), opts);
}
export function useRemoveOccurrence(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.removeOccurrence({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RemoveOccurrenceResponse(), opts);
}
export function useMoveOccurrence(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.moveOccurrence({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.MoveOccurrenceResponse(), opts);
}
export function useProposeWaiver(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.proposeOccurrenceWaiver({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ProposeOccurrenceWaiverResponse(), opts);
}
export function useProposeNotOwed(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.proposeOccurrenceNotOwed({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ProposeOccurrenceNotOwedResponse(), opts);
}
export function useApproveDecision(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.approveOccurrenceDecision({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.ApproveOccurrenceDecisionResponse(), opts);
}
export function useRejectDecision(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.rejectOccurrenceDecision({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RejectOccurrenceDecisionResponse(), opts);
}
export function useLogContractEvent(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.logContractEvent({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.LogContractEventResponse(), opts);
}
export function useCreateWorkstream(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.createWorkstream({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateWorkstreamResponse(), opts);
}
export function useCreateSow(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.createSow({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateSowResponse(), opts);
}
export function useSetExerciseWorkstream(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.setExerciseWorkstream({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.SetExerciseWorkstreamResponse(), opts);
}
export function useSetDutyAssignment(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.setDutyAssignment({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.SetDutyAssignmentResponse(), opts);
}
export function useRuleSowGap(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.ruleSowGap({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RuleSowGapResponse(), opts);
}
export function useCreateSubmission(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.createSubmission({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.CreateSubmissionResponse(), opts);
}
export function useWithdrawSubmission(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.withdrawSubmission({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.WithdrawSubmissionResponse(), opts);
}
export function useRecordReview(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.recordReview({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.RecordReviewResponse(), opts);
}
export function useUpdateDeliverableSettings(programId, opts) {
    return useCmMutation(programId, async (b)=>(await contractModelClient.updateDeliverableSettings({
            body: {
                programId,
                ...b
            }
        })).body ?? new CM.UpdateDeliverableSettingsResponse(), opts);
}
